import {CircularProgress, Grid} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {KeyboardArrowRight} from '@material-ui/icons';
import {useOnSubmitResponse} from 'hooks';
import {useGetVessel} from 'hooks/useGetEndpoint';
import usePostVesselData from 'hooks/usePostVesselData';
import React from 'react';
import {useContext, useEffect, useRef} from 'react';
import {FormProvider} from 'react-hook-form';
import {isObject} from 'utils/objects';
import {FormNavigation, FormState} from '../context';
import {defaultNext} from '../context/FormNavigation';
import {
    useFieldData,
    useFormLoopPrefix,
    useFormMethods,
    useFormPersistence,
} from '../hooks';
import {useClearVessel} from '../hooks/useClearVessel';
import {makeBool} from '../locale/utils';
import {getCurrentState} from '../utils/state';
import fields, {allVesselFields} from './fields';
import {LoopContext} from 'components/forms/context';
import get from 'lodash.get';
import Joi from 'joi';

const useStyles = makeStyles(theme => ({
    root: {
        margin: theme.spacing(1),
        flexGrow: 1,
    },
}));

const getVesselFields = (fieldsToUse, data) => {
    // takes full {raw} vessel object and returns only those fields being used by the form
    if (isObject(data)) {
        const outData = {};
        Object.entries(data).forEach(([key, value]) => {
            if (fieldsToUse.includes(key) && value !== '' && value !== null) {
                outData[key] = value;
            }
        });
        return outData;
    } else {
        return {};
    }
};

const sanitizeFormSubmissionData = inData => {
    const {imoNumber, ...outData} = getVesselFields(Object.keys(fields), inData);
    // stringify imo number...
    if (imoNumber) {
        outData.imo_number = `${outData.imo_number}`;
    }
    return outData;
};

const sanitizeFormInput = inData => {
    const {uniformed_service_vessel, ...outData} = getVesselFields(
        Object.keys(allVesselFields),
        inData
    );
    if (inData.id) {
        outData.vessel_id = inData.id;
    }
    const usv = makeBool(uniformed_service_vessel);
    outData.uniformed_service_vessel = usv === undefined ? null : `${usv}`;
    outData.tonnage =
        inData.tonnage_type === 'gt'
            ? inData?._gt_eq
            : inData?.tonnage_type === 'grt'
            ? inData?.tonnage
            : inData?.tonnage;
    return outData;
};

const VesselEndSection = ({schema, initialValues, shouldSave, ...props}) => {
    const classes = useStyles();
    const {node, navigate} = useContext(FormState);
    const {clearAndUpdate: clearVesselData} = useClearVessel(node);
    const onSubmitResponse = useOnSubmitResponse({
        node: 'VESSEL',
        redirectOnSuccess: false,
        checkSuccess: data => Object.keys(data).length,
        successText: 'Vessel Saved!',
    });
    const {state, updateState} = useFormPersistence();
    const form = useRef(null);
    // use the right method, post vs put
    const {formPost, submissionInProgress, result, error} = usePostVesselData();
    const [vesselId] = useFieldData('vessel_id');
    const {data: existingVesselData} = useGetVessel(vesselId);
    const oldVesselData = existingVesselData
        ? sanitizeFormInput(existingVesselData)
        : {};

    const looper = useContext(LoopContext);
    const loopNode = looper?.loopNode || '';
    const loopFieldHelper = useFormLoopPrefix();

    let sectionSchema = {};
    let sectionValues = {};

    // Init section with current loop data
    if (looper) {
        if (looper.parent) {
            sectionSchema = {
                [looper.parent.node]: Joi.array().items(
                    Joi.object({
                        [looper.node]: Joi.array().items(
                            Joi.object(schema).unknown(true)
                        ),
                    }).unknown(true)
                ),
            };
        } else {
            sectionSchema = {
                [looper.node]: Joi.array().items(Joi.object(schema).unknown(true)),
            };
        }

        sectionValues = Object.keys(initialValues).reduce((object, key) => {
            return {
                ...object,
                [loopFieldHelper(key)]: initialValues[key],
            };
        }, {});
    } else {
        // Not looping, continue as normal
        sectionSchema = schema;
        sectionValues = initialValues;
    }

    const methods = useFormMethods({
        schema: sectionSchema,
        initialValues: sectionValues,
    });

    const saveAndReturnLatest = () => {
        const data = methods.getValues();
        updateState(data);
        return getCurrentState(`forms[${node}]`);
    };

    const runValidations = async () => {
        if (schema && Object.keys(schema).length) {
            if (await methods.trigger()) {
                return saveAndReturnLatest();
            } else {
                // console.debug('errors', methods.errors);
                return false;
            }
        } else {
            return state || true;
        }
    };

    const nextBtn = {
        ...defaultNext,
        disabled: submissionInProgress,
        label: submissionInProgress ? '' : shouldSave ? 'Save Vessel' : 'Next',
        testHook: shouldSave ? 'form-nav-save' : 'form-nav-next',
        icon: submissionInProgress ? CircularProgress : KeyboardArrowRight,
        onClick: async () => {
            const formState = await runValidations();
            let newVesselData = {};
            if (loopNode) {
                const vesselFormState = get(formState, loopNode);
                newVesselData = sanitizeFormSubmissionData(vesselFormState);
            } else {
                newVesselData = sanitizeFormSubmissionData(formState);
            }
            // for VesselEndSection we need to grab the old state vessel state from the endpoint
            if (formState && shouldSave) {
                // either new or edited vessel, post the data
                const didPost = formPost(newVesselData, oldVesselData);
                if (!didPost) {
                    // did not need to post.. all data the same
                    // clear vessel from form state and add vessel_id back in
                    clearVesselData({
                        vessel_id: formState?.vessel_id,
                        vessel_type: formState?.vessel_type,
                        vessel_name: formState?.vessel_name,
                    });
                    // form nav using new_vessel because state is fickle
                    navigate(1, {...state, new_vessel: 'new'});
                }
            }
            if (formState && !shouldSave) {
                // form isn't done yet, go to next section (offshore)
                navigate(1, {...state, ...newVesselData});
            }
        },
    };

    const {setNextButton} = useContext(FormNavigation);

    useEffect(() => {
        setNextButton(nextBtn);
        // eslint-disable-next-line
    }, [submissionInProgress, shouldSave, existingVesselData]);

    useEffect(() => {
        onSubmitResponse({
            result: result,
            error: error,
        });
        // eslint-disable-next-line
    }, [error, result]);

    const persistData = data => {
        updateState(data);
    };

    useEffect(() => {
        if (result) {
            // clear vessel fields from state
            // and set vessel_id to whatever just came back
            clearVesselData({
                vessel_id: result?.id,
                vessel_type: result?.vessel_type,
            });
            // form nav using new_vessel (even though now it should be null) becuase state is fickle
            navigate(1, {...state, new_vessel: 'new'});
        }
        //eslint-disable-next-line
    }, [result, navigate]);

    const deepNameTransform = (children, layer = 0) => {
        return React.Children.map(children, (child, i) => {
            if (React.isValidElement(child)) {
                return React.cloneElement(child, {
                    name: loopFieldHelper(child.props.name),
                    children: deepNameTransform(child.props.children, layer + 1),
                });
            } else {
                return child;
            }
        });
    };

    return (
        <FormProvider {...methods}>
            <form
                className={classes.root}
                onSubmit={methods.handleSubmit(persistData)}
                ref={form}
            >
                <Grid
                    container
                    direction='column'
                    justifyContent='flex-start'
                    alignItems='stretch'
                    spacing={2}
                >
                    {deepNameTransform(props.children)}
                </Grid>
            </form>
        </FormProvider>
    );
};

export default VesselEndSection;
export {sanitizeFormInput};
